import { useCallback, useEffect, useRef, useState } from "react";

const KEY = "ano-thread-pane-width";
export const THREAD_PANE_MIN = 320;
export const THREAD_PANE_MAX = 760;

/** Clamp a dragged pane width to the allowed range (pure, for testing). */
export function clampThreadPaneWidth(width: number): number {
  return Math.min(
    THREAD_PANE_MAX,
    Math.max(THREAD_PANE_MIN, Math.round(width)),
  );
}

/**
 * Width + drag handle for the right-docked thread pane (wider, resizable in
 * public communities where threads are the primary content). Persists to
 * localStorage. The pane is on the RIGHT, so dragging LEFT widens it.
 */
export function useThreadPaneWidth(defaultWidth: number) {
  const [width, setWidth] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(KEY));
      return saved ? clampThreadPaneWidth(saved) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });
  const [dragging, setDragging] = useState(false);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);
  const drag = useRef<{ startX: number; startW: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    drag.current = { startX: e.clientX, startW: widthRef.current };
    setDragging(true);
    e.preventDefault();
    // Capture the pointer on the handle so subsequent move/up events keep
    // targeting it even when the cursor passes over an inline iframe (video
    // embeds in thread replies otherwise swallow the events and the drag
    // never ends — the pane stays stuck in dragging state).
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture can throw if the pointer was already released */
    }
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!drag.current) return;
      setWidth(
        clampThreadPaneWidth(
          drag.current.startW + (drag.current.startX - e.clientX),
        ),
      );
    };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null;
      setDragging(false);
      try {
        localStorage.setItem(KEY, String(widthRef.current));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    // pointercancel fires if the OS/browser steals the gesture (touch scroll,
    // tab blur) — reset just like pointerup so the drag can't get stuck.
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  return { width, onPointerDown, dragging };
}
